/**
 * Client for the `assistant` edge function. No component talks to the `agents`
 * table directly. Field names are the column names — the function passes rows
 * through unmapped. Keep in sync with `supabase/functions/assistant/types.ts`.
 */

import { api } from "@/lib/api-client";
import type { ListParams } from "@/lib/list-query";

const BASE = "/assistant";

export interface AgentConfig {
  systemPrompt: string;
  llm: {
    provider: string;
    /** Gemini 3.8 Live or a text model (shared/agent-models.json); the server stores 3.8 Live for anything else. */
    model: string;
    temperature: number;
  };
  voice: {
    voiceId: string;
    speed?: number;
    /** The Gemini voice a text-model agent speaks with (shared/agent-models.json tts_models); absent = the default.
     * Read only on a text model. */
    ttsModel?: string;
  };
  /** The accent, on either engine (shared/agent-models.json accents): "en" American, "en-IN" Indian. The server
   * keeps any other value as sent, and the bot speaks it as American. */
  language?: string;
  toolIds?: string[];
  kbStoreIds?: string[];
  /** The knowledge-base search's cutoff, 0.40-0.80 in steps of 0.05; absent = 0.55 (server default). */
  kbMinSimilarity?: number;
  /** Matches one knowledge-base search gives the agent, 1-5; absent = 3 (server default). */
  kbTopK?: number;
  /** Automatic knowledge-base search before each answer; on only when exactly true, and only on a text model. */
  kbAutoSearch?: boolean;
  /** On a text model, the words its speech recognition listens for (up to 100, 60 characters each). */
  vocabulary?: string[];
  /** Who hears the caller on a text model (shared/agent-models.json transcribers): "gemini" (also when absent) or
   * "deepgram". The server refuses any other provider; 3.8 Live keeps it and hears for itself. */
  transcriber?: { provider?: string; [key: string]: unknown };
  nativeTools?: {
    transfer_call?: {
      enabled: boolean;
      mode: "static" | "dynamic";
      staticDestination?: string;
      description?: string;
      dynamicPrompt?: string;
      callerIdMode: "agent" | "user";
      transferType: "cold" | "warm";
      sipHeaders?: Record<string, string>;
    };
    send_sms?: {
      enabled: boolean;
      description?: string;
      smsContentType?: "prompt" | "static";
      smsStaticMessage?: string;
    };
    end_call?: {
      enabled: boolean;
      description?: string;
    };
  };
  settings: {
    initialMessage?: {
      enabled: boolean;
    };
    postCallAnalysis?: {
      enabled: boolean;
      fields: Array<{
        name: string;
        description: string;
        type: "string" | "enum" | "number" | "boolean";
        required?: boolean;
        choices?: string[];
      }>;
    };
    maxCallDuration?: number;
    silenceTimeoutMinutes?: number;
    endCallOnSilence?: boolean;
    voicemailDetection?:
      | boolean
      | {
          enabled?: boolean;
          action?: "hangup" | "leave_message";
          message?: string;
        };
    affectiveDialog?: boolean;
    proactiveAudio?: boolean;
    webhookUrl?: string;
    /** How long a text model thinks before each reply (constants/liveModels THINKING_LEVELS); 3.8 Live ignores it.
     * The server stores medium, or a text model's least, when it is missing; null is legacy (the retired "none"). */
    thinkingLevel?: "minimal" | "low" | "medium" | "high" | null;
    enableGoogleSearch?: boolean;
    /** The languages the agent's callers speak, most spoken first (constants/callerLanguages): on 3.8 Live, the
     * caller transcription's language codes. Absent or empty: Gemini guesses each turn's language. */
    callerLanguages?: string[];
    [key: string]: unknown;
  };
  webWidget?: {
    enabled: boolean;
    theme: "light" | "dark" | "auto";
    primaryColor: string;
    position: "bottom-right" | "bottom-left";
    buttonSize: "small" | "medium" | "large";
    showBranding: boolean;
    avatarType?: "default" | "custom";
    avatarUrl?: string;
    integrationMode?: "floating" | "headless";
  };
}

/** One agent, whole — the only shape carrying a complete `config`. */
export interface Agent {
  id: string;
  workspace_id: string;
  agent_name: string;
  agent_type: string;
  last_edited_by: string | null;
  created_at: string;
  updated_at: string;
  config: AgentConfig;
  sip_uri?: string | null;
  livekit_sip_user?: string | null;
  has_cal_integration?: boolean;
}

/**
 * One row of the agent list — deliberately NOT an `Agent`. `config` holds only
 * `systemPrompt` and `voice`; reaching for anything else is a type error on
 * purpose. Whatever needs the whole document (the export, the editor) fetches
 * `getAssistant(id)`.
 */
export interface AgentListItem {
  id: string;
  agent_name: string;
  updated_at: string | null;
  config: Partial<Pick<AgentConfig, "systemPrompt" | "voice">>;
  inbound_phone_number: string | null;
  outbound_phone_number: string | null;
}

export interface CreateAgentBody {
  workspace_id: string;
  agent_name: string;
  agent_type: string;
  /** Omit to get the column's own DEFAULT — a real starter document. */
  config?: Partial<AgentConfig> | Record<string, unknown>;
}

/** A full replace: `config` overwrites the stored document, so callers that
 * mean to change one part read the agent first. */
export interface UpdateAgentBody {
  agent_name: string;
  agent_type?: string;
  config: Partial<AgentConfig> | Record<string, unknown>;
}

/**
 * One page of agents — the Agents table, the agent picker in six dialogs and
 * the Call History Agent filter, which differ only in whether they put a search
 * term in `params`. Nothing reads the whole list.
 *
 * A screen that merely *references* an agent does not call this: Call History
 * and the phone-number cards get the name joined onto their own row.
 */
export const listAssistants = (workspaceId: string, params: ListParams) =>
  api.postPage<AgentListItem>(`${BASE}/list`, params, { workspace_id: workspaceId });

export const getAssistant = (id: string) => api.getItem<Agent>(`${BASE}/${id}`);

/** Also provisions the agent's native `end_call` tool, server-side. */
export const createAssistant = (body: CreateAgentBody) => api.post<Agent>(BASE, body);

export const updateAssistant = (id: string, body: UpdateAgentBody) =>
  api.put<Agent>(`${BASE}/${id}`, body);

export const deleteAssistant = (id: string) => api.del(`${BASE}/${id}`);
